import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { formatToWinMultiple, shouldEnableToWinQuote } from './helpers';
import { cn } from '@/client/utils';
import { publicClient } from '@/client/utils/chain-client';
import { useBuyWin } from '@/libs/react-sdk/contracts/use-buy-win';
import { useOptions } from '@/libs/react-sdk/contracts/use-options';
import { TooltipArrow } from '@radix-ui/react-tooltip';
import { PropsWithChildren, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseUnits } from 'viem';
import { Address } from 'viem/accounts';

type ToWinTooltipProps = PropsWithChildren & {
  className?: string;
  side?: 'right' | 'top' | 'bottom' | 'left' | undefined;
  marketAddress: string;
  marketDecimal: number;
  optionAddress: string;
  optionDecimal: number;
  apiOptionList?: Array<{ optionAddress: string }>;
};

type UseToWinQuoteProps = {
  enabled: boolean;
  marketAddress: string;
  marketDecimal: number;
  optionAddress: string;
  optionDecimal: number;
  apiOptionList?: Array<{ optionAddress: string }>;
};

export const useToWinQuote = ({
  enabled,
  marketAddress,
  marketDecimal,
  optionAddress,
  optionDecimal,
  apiOptionList,
}: UseToWinQuoteProps) => {
  const optionsList = useOptions(publicClient, marketAddress as Address, { enabled });

  // Fall back to address suffix when on-chain options() reverts
  const currentOptionsIndex = useMemo(() => {
    if (!optionAddress) return -1;
    if (optionsList.data) {
      return optionsList.data.findIndex(item => item.toLowerCase() === optionAddress.toLowerCase());
    }
    if (optionsList.isError) {
      return parseInt(optionAddress.slice(-2), 16);
    }
    return -1;
  }, [optionAddress, optionsList.data, optionsList.isError, marketAddress]);
  const quoteEnabled = shouldEnableToWinQuote(enabled, currentOptionsIndex);
  const buyParams = useMemo(() => {
    if (!marketAddress || !optionAddress || currentOptionsIndex < 0) return;
    return {
      address: marketAddress as Address,
      optionIndex: BigInt(currentOptionsIndex),
      tokenAmount: parseUnits('1', marketDecimal),
      deadline: BigInt(Math.ceil(Date.now() / 1e3) + 3 * 60),
      minOptionAmount: BigInt(0),
    };
  }, [currentOptionsIndex, marketAddress, marketDecimal, optionAddress]);
  const quote = useBuyWin(publicClient, buyParams, { enabled: quoteEnabled });
  const label = useMemo(
    () => formatToWinMultiple(quote.data ?? null, optionDecimal),
    [optionDecimal, quote.data]
  );

  return {
    ...quote,
    currentOptionsIndex,
    label,
  };
};

export const ToWinTooltip = ({
  children,
  className,
  side = 'top',
  marketAddress,
  marketDecimal,
  optionAddress,
  optionDecimal,
  apiOptionList,
}: ToWinTooltipProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const quote = useToWinQuote({
    enabled: isOpen,
    marketAddress,
    marketDecimal,
    optionAddress,
    optionDecimal,
    apiOptionList,
  });

  return (
    <Tooltip open={isOpen} onOpenChange={setIsOpen}>
      <TooltipTrigger ref={ref} asChild>
        {children}
      </TooltipTrigger>
      <TooltipContent
        side={side}
        className={cn(
          'inline-flex items-center gap-.5',
          'bg-gray-800 dark:bg-gray-700 text-white text-xs font-medium leading-5 p-2.5 !animate-none text-center max-w-fit break-words',
          className
        )}
      >
        <span>{t('to-win-label')}</span>
        <span>{quote.isLoading ? '...' : quote.label ?? '--'}</span>
        <TooltipArrow className="!animate-none fill-gray-800 dark:fill-gray-700" />
      </TooltipContent>
    </Tooltip>
  );
};
